/**
 * The article editor's table tools (piece 30; decision 0800): insert a table
 * of so many rows by so many columns, add or remove a row or a column at the
 * cell the caret stands in, and turn the header row on or off. 🔨 Each one
 * rewrites the surface's own nodes and nothing else, so the markup it leaves is
 * the markup the article's allowed table keeps: a table, its head and body,
 * rows, header cells and cells, with no attribute on any of them.
 */

/** The bounds the Insert a table panel takes: a table big enough for a terminal list, small enough to read. */
export const TABLE_MAX_ROWS = 20;
export const TABLE_MAX_COLUMNS = 10;

export function tableSizeProblem(rows: number | null, columns: number | null): boolean {
  return rows === null || columns === null || !Number.isInteger(rows) || !Number.isInteger(columns) || rows < 1 || columns < 1 || rows > TABLE_MAX_ROWS || columns > TABLE_MAX_COLUMNS;
}

/** An empty cell holds a line break, so the caret can stand in it and the browser draws it at a line's height. */
const EMPTY = '<br>';

/** The markup a new table is inserted as, a paragraph after it so the author can write on below. */
export function tableHtml(rows: number, columns: number, header: boolean): string {
  const row = (cell: 'th' | 'td') => `<tr>${Array.from({ length: columns }, () => `<${cell}>${EMPTY}</${cell}>`).join('')}</tr>`;
  const bodyRows = Array.from({ length: header ? Math.max(rows - 1, 1) : rows }, () => row('td')).join('');
  return `<table>${header ? `<thead>${row('th')}</thead>` : ''}<tbody>${bodyRows}</tbody></table><p>${EMPTY}</p>`;
}

/** The cell the caret stands in, when it stands in one inside this surface. */
export function cellAt(surface: HTMLElement, node: Node | null): HTMLTableCellElement | null {
  let at: Node | null = node;
  while (at && at !== surface) {
    if (at instanceof HTMLTableCellElement) return at;
    at = at.parentNode;
  }
  return null;
}

function tableOf(cell: HTMLTableCellElement): HTMLTableElement | null {
  return cell.closest('table');
}

function newCell(kind: 'th' | 'td'): HTMLTableCellElement {
  const cell = document.createElement(kind);
  cell.innerHTML = EMPTY;
  return cell;
}

/**
 * A row below the caret's, or above it, as many cells as it has (decision
 * 1241). A row added below the header row goes to the top of the body; none is
 * ever added above the header row, which stays the table's first.
 */
export function addRow(cell: HTMLTableCellElement, where: 'above' | 'below' = 'below'): HTMLTableCellElement | null {
  const row = cell.parentElement as HTMLTableRowElement | null;
  const table = tableOf(cell);
  if (!row || !table) return null;
  const inHead = row.parentElement?.tagName === 'THEAD';
  if (inHead && where === 'above') return null;
  const fresh = document.createElement('tr');
  for (let index = 0; index < row.cells.length; index += 1) fresh.appendChild(newCell('td'));
  if (inHead) {
    const body = table.tBodies[0] ?? table.appendChild(document.createElement('tbody'));
    body.insertBefore(fresh, body.firstChild);
  } else if (where === 'above') {
    row.before(fresh);
  } else {
    row.after(fresh);
  }
  return fresh.cells[0] ?? null;
}

/** The caret's row goes; the last row going takes the table with it. */
export function removeRow(cell: HTMLTableCellElement): void {
  const row = cell.parentElement;
  const table = tableOf(cell);
  if (!row || !table) return;
  const section = row.parentElement;
  row.remove();
  if (section && section !== table && section.children.length === 0) section.remove();
  if (table.rows.length === 0) table.remove();
}

/** A column to the right of the caret's, or to its left: a header cell in the header row and an ordinary cell below it (decision 1241). */
export function addColumn(cell: HTMLTableCellElement, side: 'left' | 'right' = 'right'): void {
  const table = tableOf(cell);
  if (!table) return;
  const index = cell.cellIndex;
  for (const row of Array.from(table.rows)) {
    const kind = row.parentElement?.tagName === 'THEAD' ? 'th' : 'td';
    const beside = row.cells[Math.min(index, row.cells.length - 1)];
    if (!beside) row.appendChild(newCell(kind));
    else if (side === 'left' && index < row.cells.length) beside.before(newCell(kind));
    else beside.after(newCell(kind));
  }
}

/** The caret's column goes from every row; the last column going takes the table with it. */
export function removeColumn(cell: HTMLTableCellElement): void {
  const table = tableOf(cell);
  if (!table) return;
  const index = cell.cellIndex;
  for (const row of Array.from(table.rows)) row.cells[Math.min(index, row.cells.length - 1)]?.remove();
  if (Array.from(table.rows).every((row) => row.cells.length === 0)) table.remove();
}

function retag(cell: HTMLTableCellElement, kind: 'th' | 'td'): HTMLTableCellElement {
  const next = document.createElement(kind);
  while (cell.firstChild) next.appendChild(cell.firstChild);
  const style = cell.getAttribute('style');
  if (style) next.setAttribute('style', style);
  cell.replaceWith(next);
  return next;
}

/** The header row on or off: the first row moves into the table's head as header cells, or back into its body as cells. */
export function toggleHeaderRow(cell: HTMLTableCellElement): void {
  const table = tableOf(cell);
  if (!table) return;
  const head = table.tHead;
  if (head) {
    const body = table.tBodies[0] ?? table.appendChild(document.createElement('tbody'));
    for (const row of Array.from(head.rows).reverse()) {
      for (const each of Array.from(row.cells)) retag(each, 'td');
      body.insertBefore(row, body.firstChild);
    }
    head.remove();
    return;
  }
  const first = table.rows[0];
  if (!first) return;
  const created = document.createElement('thead');
  for (const each of Array.from(first.cells)) retag(each, 'th');
  created.appendChild(first);
  table.insertBefore(created, table.firstChild);
}

/** What the table bar shows of a table (decision 1240): its rows, its widest row's cells, whether it has a header row, and whether the caret's cell stands in it. */
export function tableFactsOf(table: HTMLTableElement, cell?: HTMLTableCellElement | null): { rows: number; columns: number; header: boolean; inHeader: boolean } {
  const rows = Array.from(table.rows);
  return {
    rows: rows.length,
    columns: Math.max(0, ...rows.map((row) => row.cells.length)),
    header: table.tHead !== null && table.tHead.rows.length > 0,
    inHeader: cell?.parentElement?.parentElement?.tagName === 'THEAD',
  };
}

/**
 * 🔒 Why a table bar press cannot act, or null where it can (decision 1241):
 * the bounds Insert a table takes, the one row and the one column a table
 * keeps, and no row above the header row.
 */
export type TableRefusal = 'maxRows' | 'maxColumns' | 'lastRow' | 'lastColumn' | 'aboveHeader';
export function tableRefusal(action: 'rowAbove' | 'rowBelow' | 'rowRemove' | 'columnLeft' | 'columnRight' | 'columnRemove', facts: { rows: number; columns: number; inHeader: boolean }): TableRefusal | null {
  if (action === 'rowAbove' && facts.inHeader) return 'aboveHeader';
  if ((action === 'rowAbove' || action === 'rowBelow') && facts.rows >= TABLE_MAX_ROWS) return 'maxRows';
  if ((action === 'columnLeft' || action === 'columnRight') && facts.columns >= TABLE_MAX_COLUMNS) return 'maxColumns';
  if (action === 'rowRemove' && facts.rows <= 1) return 'lastRow';
  if (action === 'columnRemove' && facts.columns <= 1) return 'lastColumn';
  return null;
}

/** The cell Tab moves to from this one, forward or back through the rows in order, or null past either end (decision 1241). */
export function neighbourCell(cell: HTMLTableCellElement, back: boolean): HTMLTableCellElement | null {
  const table = tableOf(cell);
  if (!table) return null;
  const cells = Array.from(table.rows).flatMap((row) => Array.from(row.cells));
  const at = cells.indexOf(cell);
  return cells[back ? at - 1 : at + 1] ?? null;
}
